import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { FullScreenMoreMenu } from '../More/FullScreenMoreMenu';
import { FeeStructure as AppFeeStructure, IRawFeeStructure } from '../../types/global/helper';
import { isEmpty } from '../../utils/lodash';
import { DEFAULT_CURRENCY, formatCurrency, getPriceForDisplay } from '../../constants/currency';

// Custom interface for displaying fee structures
export interface FeeCardProps {
    fee: AppFeeStructure & {
        name?: string;
        description?: string;
        amount?: number;
        type?: 'percentage' | 'fixed';
        applies_to?: 'per_ticket' | 'per_order';
        is_active?: boolean;
    };
    actions?: Array<{ id: string; title: string }>;
    onActionSelect?: (action: string, fee: IRawFeeStructure) => void;
    manageMode?: boolean;
}

export const FeeCard: React.FC<FeeCardProps> = ({ fee, actions = [], onActionSelect, manageMode = false }) => {
    const { themeStyle } = useThemeContext();

    // Parse fee string to get amount and type if not explicitly provided
    const getFeeDetails = () => {
        let amount = manageMode ? (Number(fee.fee) / 100).toFixed() : fee.amount || 0;
        let type: 'percentage' | 'fixed' = fee.type || 'fixed';

        if (!fee.amount && fee.fee && typeof fee.fee === 'string') {
            if (fee.fee.includes('%')) {
                type = 'percentage';
                amount = parseFloat(fee.fee.replace('%', ''));
            } else {
                type = 'fixed';
                amount = parseFloat(fee.fee.replace(/[^0-9.]/g, ''));
            }
        }

        return { amount, type };
    };

    // Get the appropriate currency code to display
    const getDisplayCurrency = (): string => {
        // Prioritize transaction_currency if available
        return (fee.transaction_currency || fee.currency || DEFAULT_CURRENCY).toLowerCase();
    };

    const { amount } = getFeeDetails();
    const displayCurrency = getDisplayCurrency();
    const isFree = amount === 0;
    const isDiscounted = !isEmpty(fee.discount_message);

    return (
        <Box
            sx={{
                position: 'relative',
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}>
            {/* Ticket stub container with notches */}
            <Box
                sx={{
                    position: 'relative',
                    height: '100%',
                    width: '100%',
                    backgroundColor: theme[themeStyle].table_header,
                    borderRadius: 2,
                    border: `1px solid ${theme[themeStyle].placeholderGray}`,
                    overflow: 'visible',

                    // Create the semi-circular notches on left and right sides
                    '&::before, &::after': {
                        content: '""',
                        position: 'absolute',
                        width: '20px',
                        height: '40px',
                        backgroundColor: theme[themeStyle].zeus_dark,
                        borderRadius: '20px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 1,
                    },
                    '&::before': {
                        left: '-10px',
                        borderRight: `1px solid ${theme[themeStyle].placeholderGray}`,
                    },
                    '&::after': {
                        right: '-10px',
                        borderLeft: `1px solid ${theme[themeStyle].placeholderGray}`,
                    },
                }}>
                {/* Main ticket content container - always row layout */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        height: '100%',
                        position: 'relative',
                    }}>
                    {/* Left side - Ticket details */}
                    <Box
                        sx={{
                            flex: 1,
                            p: 2,
                            position: 'relative',
                            minWidth: 0, // Allows content to shrink below min-content width
                        }}>
                        {actions.length > 0 && onActionSelect && (
                            <Box sx={{ position: 'absolute', top: 10, right: 10, zIndex: 2 }}>
                                <FullScreenMoreMenu
                                    menu={actions}
                                    onClick={action => onActionSelect(action, fee as unknown as IRawFeeStructure)}
                                />
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: { xs: '0.9rem', sm: '1rem' },
                                    color: theme[themeStyle].primary_white,
                                    mb: 1,
                                    maxWidth: { xs: '70%', sm: '80%' },
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}>
                                {fee.fee_name || fee.name || 'Unnamed Fee'}
                            </Typography>

                            {fee.is_active !== undefined && (
                                <Chip
                                    label={fee.is_active ? 'Active' : 'Inactive'}
                                    size="small"
                                    sx={{
                                        backgroundColor: fee.is_active
                                            ? theme[themeStyle].primaryGreen
                                            : theme[themeStyle].primary_red,
                                        color: theme[themeStyle].primary_white,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: '0.75rem',
                                        height: '24px',
                                    }}
                                />
                            )}
                        </Box>

                        {fee.description && (
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    fontSize: { xs: '0.75rem', sm: '0.85rem' },
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 2,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                }}>
                                {fee.description}
                            </Typography>
                        )}

                        {fee.fee_features && fee.fee_features.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                <ul
                                    style={{
                                        margin: 0,
                                        paddingLeft: '20px',
                                        listStyleType: 'disc',
                                    }}>
                                    {fee.fee_features.map((feature, index) => (
                                        <li
                                            key={index}
                                            style={{
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                fontSize: '0.75rem',
                                                color: theme[themeStyle].light_gray_text,
                                                marginBottom: '4px',
                                            }}>
                                            {feature}
                                        </li>
                                    ))}
                                </ul>
                            </Box>
                        )}

                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                                color: theme[themeStyle].light_gray_text,
                                mt: 'auto',
                                pt: 1,
                            }}>
                            ID: {fee._id?.substring(0, 8).toUpperCase() || 'N/A'}
                        </Typography>
                    </Box>

                    {/* Vertical divider - consistent across all screen sizes */}
                    <Box
                        sx={{
                            position: 'relative',
                            width: 0,
                            borderLeft: `2px dashed ${theme[themeStyle].placeholderGray}`,
                            height: '80%',
                            alignSelf: 'center',
                            margin: '0 8px',
                            zIndex: 5,
                        }}
                    />

                    {/* Right side - Price and actions */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            p: { xs: 1, sm: 2 },
                            width: { xs: '30%', sm: '35%' },
                            maxWidth: { xs: '120px', sm: '160px' },
                            minWidth: { xs: '90px' },
                            alignItems: 'center',
                            position: 'relative',
                        }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: { xs: '0.8rem', sm: '1.1rem' },
                                color: theme[themeStyle].primary_orange,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '5px',
                                mb: 1,
                                textAlign: 'center',
                                wordBreak: 'break-word',
                            }}>
                            {isFree ? 'Free' : getPriceForDisplay(fee, formatCurrency(Number(amount), displayCurrency))}
                        </Typography>

                        {isDiscounted && (
                            <Typography
                                sx={{
                                    color: theme[themeStyle].light_gray_text,
                                    fontSize: { xs: '0.75rem', sm: '0.85rem' },
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    textDecoration: 'line-through',
                                    mb: 1,
                                    textAlign: 'center',
                                }}>
                                {getPriceForDisplay(fee, formatCurrency(fee.original_price || 0, displayCurrency))}
                            </Typography>
                        )}

                        {fee.applies_to && (
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                    color: theme[themeStyle].light_gray_text,
                                    textTransform: 'capitalize',
                                    textAlign: 'center',
                                }}>
                                {fee.applies_to.replace('_', ' ')}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
